import React, { useEffect, RefObject } from 'react';
import {
  useFloating,
  useInteractions,
  shift,
  offset as floatingOffset,
  flip,
  useClick,
  useDismiss,
  autoUpdate,
  Placement,
  FloatingPortal,
} from '@floating-ui/react';

interface PopoverProps {
  anchorRef: RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  position?: 'top' | 'down' | 'start' | 'end';
  children: React.ReactNode;
  className?: string;
  offset?: number;
  showClose?: boolean;
  headerText?: React.ReactNode;
  persist?: boolean;
}

// Map position prop to floating-ui placement
const positionToPlacement = (position: 'top' | 'down' | 'start' | 'end'): Placement => {
  switch (position) {
    case 'top':
      return 'top';
    case 'down':
      return 'bottom';
    case 'start':
      return 'left';
    case 'end':
      return 'right';
    default:
      return 'bottom';
  }
};

export const Popover = ({
  anchorRef,
  open,
  onClose,
  position = 'down',
  children,
  className = '',
  offset = 8,
  showClose = false,
  headerText,
  persist = false,
}: PopoverProps) => {
  const { refs, x, y, strategy, context } = useFloating({
    open,
    onOpenChange: (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    placement: positionToPlacement(position),
    middleware: [shift(), flip(), floatingOffset(offset || 8)],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { enabled: !persist });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const floatingStyles: React.CSSProperties = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
  };

  // Set the anchor ref as reference
  useEffect(() => {
    if (anchorRef.current) {
      refs.setReference(anchorRef.current);
    }
  }, [anchorRef, refs]);

  // Add escape key listener similar to Modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [open, onClose]);

  if (!open) return null;

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        className={`relative z-[1000] rounded-md border border-gray-200 bg-red-950 p-3 shadow-lg ${className}`}
        {...getFloatingProps()}
      >
        {showClose && (
          <button
            onClick={onClose}
            className="absolute right-2 top-2 text-gray-400 hover:text-gray-200"
            aria-label="Close popover"
          >
            ✕
          </button>
        )}
        {headerText &&
          (typeof headerText === 'string' ? (
            <h3 className={`mb-2 font-semibold text-white ${showClose ? 'pr-8' : ''}`}>
              {headerText}
            </h3>
          ) : (
            <div className={`mb-2 ${showClose ? 'pr-8' : ''}`}>{headerText}</div>
          ))}
        <div className={showClose && !headerText ? 'pt-4' : ''}>{children}</div>
      </div>
    </FloatingPortal>
  );
};
